import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { cn } from "@/lib/utils";

import { getSupabaseClient } from "../../providers/supabase/supabase";
import { AnalystCard } from "./AnalystCard";
import { AdsCard, LostAndPaidCard, SegmentsCard } from "./BreakdownCards";
import { FunnelCard, HoursCard, SpeedCard } from "./FunnelCard";
import { KpiTile } from "./KpiTile";
import {
  PERF_PERIODS,
  delta,
  euros,
  isMaturing,
  keyRates,
  minutes,
  percent,
  perfRange,
  previousRange,
  type DayRange,
  type PerfPeriodKey,
  type Performance,
} from "./performance";
import { SettersCard } from "./SettersCard";

const fetchPerformance = async (range: DayRange) => {
  const { data, error } = await getSupabaseClient().rpc("gula_performance", {
    p_from: range.from,
    p_to: range.to,
  });
  if (error) throw error;
  return data as Performance;
};

const usePerformance = (range: DayRange) =>
  useQuery({
    queryKey: ["gula-performance", range.from, range.to],
    queryFn: () => fetchPerformance(range),
    staleTime: 60_000,
  });

const shortDay = (day: string) =>
  new Date(`${day}T12:00:00`).toLocaleDateString("es-ES", {
    day: "numeric",
    month: "short",
  });

/** Marketing → Rendimiento: whether the business is selling well, and why. */
export const PerformancePage = () => {
  const [period, setPeriod] = useState<PerfPeriodKey>("30d");
  const range = perfRange(period);
  const before = previousRange(range);
  const current = usePerformance(range);
  const previous = usePerformance(before);

  const perf = current.data;
  const rates = perf ? keyRates(perf) : null;
  const old = previous.data ? keyRates(previous.data) : null;
  const s = perf?.summary;
  const days =
    Math.round(
      (new Date(`${range.to}T12:00:00`).getTime() -
        new Date(`${range.from}T12:00:00`).getTime()) /
        864e5,
    ) + 1;

  return (
    <div className="mx-auto flex w-full max-w-7xl flex-col gap-6 px-1 pb-16 pt-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Rendimiento</h1>
          <p className="text-sm text-muted-foreground">
            Cómo convierte el negocio: de lead a venta, por anuncio, tipo de
            lead y persona. {shortDay(range.from)} – {shortDay(range.to)},
            comparado con {shortDay(before.from)} – {shortDay(before.to)}.
          </p>
        </div>
        <div className="flex rounded-full border p-1">
          {PERF_PERIODS.map((p) => (
            <button
              key={p.key}
              type="button"
              onClick={() => setPeriod(p.key)}
              className={cn(
                "rounded-full px-3 py-1 text-sm",
                period === p.key
                  ? "bg-foreground text-background"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {current.isError ? (
        <p className="rounded-2xl border border-red-300 bg-red-50 p-4 text-sm text-red-900 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-100">
          No se han podido cargar los números.
        </p>
      ) : null}
      {current.isPending ? (
        <p className="text-sm text-muted-foreground">Calculando…</p>
      ) : null}

      {perf && rates && s ? (
        <>
          {isMaturing(perf) ? (
            <p className="rounded-2xl bg-muted p-3 text-sm text-muted-foreground">
              Los leads de los últimos días aún pueden comprar (una venta tarda
              una mediana de{" "}
              {s.days_to_sale_median?.toLocaleString("es-ES") ?? "2–3"} días),
              así que el cierre de este periodo todavía puede subir.
            </p>
          ) : null}

          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <KpiTile
              label="Leads"
              value={String(s.leads)}
              detail={`${(s.leads / days).toLocaleString("es-ES", { maximumFractionDigits: 1 })} al día`}
              change={
                previous.data ? s.leads - previous.data.summary.leads : null
              }
            />
            <KpiTile
              label="Cierre (ventas / leads)"
              value={percent(rates.closeRate)}
              raw={rates.closeRate}
              metric="closeRate"
              detail={`${s.sold} ventas de ${s.leads} leads`}
              change={delta(rates.closeRate, old?.closeRate ?? null)}
              changeUnit=" pts"
            />
            <KpiTile
              label="Hablamos con"
              value={percent(rates.contactRate)}
              raw={rates.contactRate}
              metric="contactRate"
              detail={`${s.talked} de ${s.leads} leads`}
              change={delta(rates.contactRate, old?.contactRate ?? null)}
              changeUnit=" pts"
            />
            <KpiTile
              label="Cierre de los hablados"
              value={percent(rates.closeTalked)}
              raw={rates.closeTalked}
              metric="closeTalked"
              detail="Cuánto vendemos cuando hablamos"
              change={delta(rates.closeTalked, old?.closeTalked ?? null)}
              changeUnit=" pts"
            />
            <KpiTile
              label="Ventas cerradas en el periodo"
              value={String(perf.sales.orders)}
              detail={`${euros(perf.sales.revenue)} · ticket medio ${euros(perf.sales.avg_ticket)}`}
              change={
                previous.data
                  ? perf.sales.orders - previous.data.sales.orders
                  : null
              }
            />
            <KpiTile
              label="Tiempo hasta la 1ª llamada"
              value={minutes(rates.speedMinutes)}
              raw={rates.speedMinutes}
              metric="speedMinutes"
              detail={`${percent(rates.within60)} llamados en menos de 1 h`}
              change={delta(rates.speedMinutes, old?.speedMinutes ?? null)}
              changeUnit=" min"
            />
            <KpiTile
              label="Llamadas cogidas"
              value={percent(rates.pickupRate)}
              raw={rates.pickupRate}
              metric="pickupRate"
              detail={`${perf.activity.answered} de ${perf.activity.calls} llamadas`}
              change={delta(rates.pickupRate, old?.pickupRate ?? null)}
              changeUnit=" pts"
            />
            <KpiTile
              label="Leads sin hablar nunca"
              value={percent(rates.unreachedRate)}
              raw={rates.unreachedRate}
              metric="unreachedRate"
              detail={`${s.unreached} leads: dinero de anuncios perdido`}
              change={delta(rates.unreachedRate, old?.unreachedRate ?? null)}
              changeUnit=" pts"
            />
            {perf.spend ? (
              <>
                <KpiTile
                  label="Gasto en Meta"
                  value={euros(perf.spend.total)}
                  detail={`${perf.spend.meta_leads} leads según Meta`}
                  change={
                    previous.data?.spend
                      ? Math.round(perf.spend.total - previous.data.spend.total)
                      : null
                  }
                  changeUnit=" €"
                  moreIsBetter={false}
                />
                <KpiTile
                  label="Coste por lead"
                  value={euros(rates.costPerLead, 2)}
                  change={delta(rates.costPerLead, old?.costPerLead ?? null)}
                  changeUnit=" €"
                  moreIsBetter={false}
                />
                <KpiTile
                  label="Coste por venta"
                  value={euros(rates.costPerSale)}
                  raw={rates.costShare}
                  metric="costShare"
                  detail={`${percent(rates.costShare)} del ticket medio`}
                  change={delta(rates.costPerSale, old?.costPerSale ?? null)}
                  changeUnit=" €"
                />
                <KpiTile
                  label="Retorno (facturado / gasto)"
                  value={
                    rates.roas == null
                      ? "—"
                      : `${rates.roas.toLocaleString("es-ES")}x`
                  }
                  raw={rates.roas}
                  metric="roas"
                  change={delta(rates.roas, old?.roas ?? null)}
                  changeUnit="x"
                />
              </>
            ) : null}
          </div>

          {!perf.spend ? (
            <p className="rounded-2xl border border-dashed p-3 text-sm text-muted-foreground">
              Falta conectar Meta Ads al CRM para ver el gasto, el coste por
              lead, el coste por venta y el retorno de cada anuncio.
            </p>
          ) : null}

          <div className="grid gap-4 lg:grid-cols-[1.4fr_1fr]">
            <FunnelCard perf={perf} />
            <AnalystCard range={range} />
          </div>
          <div className="grid gap-4 lg:grid-cols-2">
            <SpeedCard perf={perf} />
            <HoursCard perf={perf} />
          </div>
          <SettersCard perf={perf} />
          <AdsCard perf={perf} />
          <SegmentsCard perf={perf} />
          <LostAndPaidCard perf={perf} />
        </>
      ) : null}
    </div>
  );
};

PerformancePage.path = "/rendimiento";
